import { FeatureCTASectionPropType } from "@/components/Features/FeatureCTASection";
import { CommunityPlatformSectionType } from "@/components/PricingPage/CommunityPlatformSection";
import {
  GraphyPlanType,
  PaidFeaturesSectionPropType
} from "@/components/PricingPage/PaidFeaturesSection";
import { PricingHeroSectionPropType } from "@/components/PricingPage/PricingHeroSection";
import { PricingOfferingsSectionPropType } from "@/components/PricingPage/PricingOfferingsSection";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  CtaActionType,
  SignUpSourceEnum
} from "@/components/molecules/CtaButtonGroup";
import { PricingMarqueeSectionPropType } from "@/components/organisms/Carousels/PricingMarqueeSection";
import { FaqSectionPropsType } from "@/components/organisms/FaqSection";
import { GraphyHeadPropType } from "@/components/organisms/GraphyHead";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { CtaVisitHelpCentre_en } from "../constants/common.constants";
import { HeroPillCategoryEnum_en } from "./hero-pills.data";
import { ROUTE_PATHS } from "./routes";

import { BillingCycleType } from "@/components/PricingPage/PricingComparisonSection";
import { OnboardingDestinationEnum } from "../types/common.types";

export type PricingPageDataType = {
  seoMeta: GraphyHeadPropType;
  pricingHeroSectionProps?: PricingHeroSectionPropType;
  // estimationSectionProps: EstimationSectionPropType;
  paidFeaturesSectionProps?: PaidFeaturesSectionPropType;
  communityPlatformSectionProps?: CommunityPlatformSectionType;
  pricingMarqueeSectionProps?: PricingMarqueeSectionPropType | null;
  faqSectionProps: FaqSectionPropsType;
  pricingOfferingsSectionProps: PricingOfferingsSectionPropType;
  featureCTASectionProps: FeatureCTASectionPropType;
  scheduleDemoSectionProps?: any;
  featuresSectionProps?: any;
  planComparisonSectionProps?: any;
  featureHighlightBar?: { titles: string[]; starIcon: string };
  graphyAssistSectionProps?: any;
  platformMigrationBannerProps?: any;
  reachOutPricingSectionProps?: any;
  testimonySectionProps?: { title: string };
};

export const pricingPageData_en: PricingPageDataType = {
  seoMeta: {
    title: "Graphy Pricing | Start Earning As A Creator Today",
    description:
      "Explore free trials and paid plans. Compare Plans. No fine print. No hidden limits. Launch plan ₹1,999 per month, growth plan ₹4,200 per month and rise plan ₹8,400 per month.",
    canonicalPath: ROUTE_PATHS.PRICING,
    countryCode: "",
  },
  pricingHeroSectionProps: {
    heroPill: {
      category: HeroPillCategoryEnum_en.Pricing,
    },
    title: [
      {
        text: "Try out Graphy for free for 14 days",
      },
      {
        text: "",
        highlight: true,
      },
    ],
    description:
      "Whether you're a solo creator or a team of 10, Graphy gives you everything you need to launch, scale, and automate your online teaching business — all in one place.",
    freeCardDetails: {
      title: [
        {
          text: "10% per sale",
          highlight: true,
        },
      ],
      subtitle: "Setup in less than 5 minutes",
      description: "",
      cta: {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        actionType: CtaActionType.SignUp,
        text: "Get started now",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.hero,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      pointsHeading: "You’ll get access to:",
      points: [
        "Your own website",
        "Tools to build and sell digital products",
        "AI avatar to handle sales, queries, and engagement",
        "Secure payment gateway and CRM",
      ],
      cardTitle: "Create your course business for free",
      cardImage: "/images/icons/price-heroen.svg", 
      oldPrice: { india: "Rs. 24999", global: "$49" },
      newPrice: "Free",
      trialText: "for 14 days",
      ctaText: "Start your 14-day free trial",
      subtext: "No credit card required",
      startsAtText: "Starts at"
    },
    ratings: [
      {
        logo: "/images/categories/home-testimonials/g2-crowd.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
      {
        logo: "/images/categories/home-testimonials/capterra.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
      {
        logo: "/images/categories/home-testimonials/trustpilot.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
    ],
  },
  featureHighlightBar: {
    titles: [
      "Unlimited Products",
      "Unlimited Learners",
      "Branded mobile Apps",
      "AI Avatar",
      "Secure Payments",
      "no code website"
    ],
    starIcon: "/images/icons/starprice.svg"
  },
  featuresSectionProps: {
    title: "Every paid plan includes",
    features: [
      "Free android app",
      "Custom domain for your website",
      "Third party apps integration",
      "Razorpay payment gateway",
      "Host 1 live session at the same time *",
      "Live chat and email support",
    ],
    cta: {
      type: ButtonTypeEnum.Primary,
      size: ButtonSizeEnum.Regular,
      actionType: CtaActionType.SignUp,
      text: "Get Started",
      amplitudeEvent: {
        eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
        eventProperties: {
          source: SignUpSourceEnum.body_end,
        },
      },
      queryParams: {
        destination: OnboardingDestinationEnum.PricingLaunchMonthly,
        skipOnboarding: false,
      },
    },
  },
  graphyAssistSectionProps: {
    title: [
      { text: "Build your AI Brain", highlight: false }
    ],
    description: "Turn your videos, PDFs, tutorials, and FAQs into a powerful AI that knows your business inside out — and runs it like you would.",
    features: [
      "Your own sales, support & engage avatars trained on your content",
      "Custom avatar options that think, talk, and act like you",
      "No extra hiring — just scale yourself",
    ],
    image: {
      src: "/images/pricing-Buildavatar.svg",
      alt: "Graphy Assist",
    },
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: "Train your AI brain",
        actionType: CtaActionType.SignUp,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.ai_avatar,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      {
        type: ButtonTypeEnum.Link,
        size: ButtonSizeEnum.Regular,
        text: "See It in Action",
        actionType: CtaActionType.DelayedHref,
        href: "/ai-avatar",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.LEARN_MORE_TAPPED.LABEL,
          eventProperties: {
            source: "ai_avatar",
          },
        },
      },
    ],
    footerText: "While you sleep, teach, or take a break — your AI sells, supports, and re-engages learners for you."
  },
  // estimationSectionProps: {
  //   title: [
  //     {
  //       text: "Access your course ",
  //       highlight: false,
  //     },
  //     {
  //       text: " potential",
  //       highlight: true,
  //     },
  //   ],
  //   description: "Know your worth and harness the power of your creations",
  //   ctas: [
  //     {
  //       type: ButtonTypeEnum.Gradient,
  //       size: ButtonSizeEnum.Regular,
  //       text: "Start earning now",
  //       actionType: CtaActionType.SignUp,
  //       amplitudeEvent: {
  //         eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
  //         eventProperties: {
  //           source: SignUpSourceEnum.earnings_slider_section,
  //         },
  //       },
  //       queryParams: {
  //         destination: OnboardingDestinationEnum.PricingLaunchMonthly,
  //         skipOnboarding: true,
  //       },
  //     },
  //   ],
  //   audienceSize: {
  //     title: "How big is your audience",
  //     description: "Starting from scratch? Let's estimate your initial base",
  //     defaultValue: 1000,
  //     maximumValue: 10000,
  //   },
  //   offeringPrice: {
  //     title: "Price your offerings",
  //     description: "Estimate the fair price for your valuable content",
  //     defaultValue: 1000,
  //     maximumValue: 10000,
  //   },
  //   perYear: "per year",
  // },
  // paidFeaturesSectionProps: {
  //   title: [
  //     {
  //       text: "Unlock all features ",
  //       highlight: true,
  //     },
  //     {
  //       text: "to fuel your success",
  //     },
  //   ],
  //   countryCode: "", // Comes from the page props
  //   plans: [
  //     {
  //       planName: GraphyPlanType.Launch,
  //       amount: { india: "₹24,999", global: "$499" },
  //       billingCycle: "per year",
  //       graphyShare: { value: "10%", text: "per sale" },
  //       bestFor: "Best choice to start your business",
  //       cta: {
  //         type: ButtonTypeEnum.Primary,
  //         size: ButtonSizeEnum.Regular,
  //         actionType: CtaActionType.SignUp,
  //         text: "Get started",
  //         amplitudeEvent: {
  //           eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
  //           eventProperties: {
  //             source: SignUpSourceEnum.pricing_section,
  //           },
  //         },
  //         queryParams: {
  //           destination: OnboardingDestinationEnum.PricingLaunchYearly,
  //           skipOnboarding: true,
  //         },
  //       },
  //     },
  //     {
  //       planName: GraphyPlanType.Rise,
  //       amount: { india: "₹1,19,999", global: "$1499" },
  //       billingCycle: "per year",
  //       graphyShare: { value: "5%", text: "per sale" },
  //       bestFor: "Best for established creators",
  //       cta: {
  //         type: ButtonTypeEnum.Secondary,
  //         size: ButtonSizeEnum.Regular,
  //         actionType: CtaActionType.BookDemo,
  //         utmFields: {
  //           utm_medium: "riseplan",
  //         },
  //         text: "Talk to us",
  //         amplitudeEvent: {
  //           eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
  //           eventProperties: {
  //             source: BookADemoSourceEnum.pricing_section,
  //           },
  //         },
  //       },
  //     },
  //   ],

  //   points: [
  //     "Build your own Android & iOS app *",
  //     "Custom domain for your website",
  //     "Third party apps integration",
  //     "Razorpay payment gateway",
  //     "Host 3 live sessions at the same time *",
  //   ],
  // },
  // communityPlatformSectionProps: {
  //   title: "Looking for a platform to host your community?",
  //   description:
  //     "Everything you need to manage, engage, and monetize your community",
  //   points: [
  //     {
  //       icon: "/images/icons/cloud.svg",
  //       text: "Seemless integration with Graphy, including app-in-app support",
  //     },
  //     {
  //       icon: "/images/icons/chat.svg",
  //       text: "Keep your audience engaged through Live events, Chat, forums and much more",
  //     },
  //     {
  //       icon: "/images/icons/coins.svg",
  //       text: "Gamification to reward your members for their active participation",
  //     },
  //   ],
  //   image: {
  //     src: GraphyCommunityImage,
  //     alt: "Graphy Community",
  //   },
  //   ctas: [
  //     {
  //       type: ButtonTypeEnum.GradientBlue,
  //       size: ButtonSizeEnum.Regular,
  //       text: "Talk to us",
  //       target: "_blank",
  //       href: GRAPHY_COMMUNITY_CALENDLY_LINK,
  //     },
  //   ],
  // },
  pricingMarqueeSectionProps: {
    title: [
      {
        text: "Feedback from our creators",
      },
    ],
    successStories: [
      // will be infered from creator-stories.data.ts
    ],
  },
  faqSectionProps: {
    title: "FAQs",
    description:
      "Explore our help center for detailed information, troubleshooting guides, and more",
    cta: CtaVisitHelpCentre_en,
    faqs: [
      {
        question:
          "Do you take 10% on any type of sale or is it different for different offerings?",
        answer:
          "We take a flat 10% revenue share for all types of offerings, subject to some ongoing offers where we might charge even less. ",
      },
      {
        question: "Is there a refund policy?",
        answer:
          "Yes, if you end up not finding a use case for the platform, we offer a refund window of 7 days, no questions asked",
      },
      {
        question: "How can I pay for my Graphy account?",
        answer:
          "We have support for almost all the payment methods in different geographies. Checkout your Graphy dashboard to know more.",
      },
      {
        question: "Are there any academic discounts on Graphy?",
        answer:
          "Please contact our team for more information on any ongoing discounts",
      },
      {
        question: "How does the payout work? ",
        answer:
          "All your earnings from your sales will be instantly credited into your account.",
      },
    ],
  },
  pricingOfferingsSectionProps: {
    title: "Check out our offerings",
    description: "Check Out Offerings That Will Help You Grow",
    productOfferings: [], // Will be inferred
    featureOfferings: [], // Will be inferred
  },
  featureCTASectionProps: {
    title: [
      {
        text: "Let's start your",
        highlight: false,
      },
      {
        text: " journey",
        highlight: true,
      },
    ],
    description:
      "Unleash your entrepreneurial spirit. Launch your online business with Graphy and seize success on your own terms",
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        actionType: CtaActionType.SignUp,
        text: "Start for free",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.body_end,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      {
        type: ButtonTypeEnum.Secondary,
        size: ButtonSizeEnum.Regular,
        text: "Book a Demo",
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.body_end,
          },
        },
      },
    ],
  },
  planComparisonSectionProps: {
    heroPill: {
      category: HeroPillCategoryEnum_en.Pricing,
    },
    title: [
      {
        text: "Compare Plans",
        highlight: false,
      },
    ],
    description: "",
    subDescription: "No fine print. No hidden limits. Just choose your plan and start.",
    billingCycles: [
      { value: BillingCycleType.Year, label: "Billed annually" },
    ],
    DiscountText: "",
    plans: [
      {
        planName: GraphyPlanType.Launch,
        amount: { year: "₹1,999", global: "$49" },
        mainPrice: "₹1,999",
        priceSuffix: "per month",
        bestFor: "Tailored for creators",
        recommened: false,
        features: [
          { india: "10%", global: "10%" },
          { india: "or ₹10 whichever is higher", global: "or $0.25 whichever is higher" },
          "Best choice to start your business",
        ],
        // Move CTA to a new field for rendering below the card
        ctasBelow: [
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            actionType: CtaActionType.SignUp,
            text: "Get Started",
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
              eventProperties: {
                source: SignUpSourceEnum.pricing_section,
              },
            },
            queryParams: {
              destination: OnboardingDestinationEnum.PricingLaunchYearly,
              skipOnboarding: false,
            },
          },
        ],
        ctas: [], // Remove from main card
      },
      {
        planName: GraphyPlanType.Rise,
        amount: { year: "₹9,999", global: "$125" },
        mainPrice: "₹9,999",
        priceSuffix: "per month",
        bestFor: "Built for businesses",
        recommened: true,
        features: [
          { india: "5%", global: "5%" },
          { india: "or ₹10 whichever is higher", global: "or $0.25 whichever is higher" },
          "Best for established creators",
        ],
        ctasBelow: [
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            actionType: CtaActionType.SignUp,
            text: "Get Started",
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
              eventProperties: {
                source: SignUpSourceEnum.pricing_section,
              },
            },
            queryParams: {
              destination: OnboardingDestinationEnum.PricingRiseYearly,
              skipOnboarding: false,
            },
          },
        ],
        ctas: [], // Remove from main card
      },
    ],
  },
  platformMigrationBannerProps: {
    title: [
      { text: 'Already using another platform?', highlight: true },
      { text: 'We’ll move everything over for you.', highlight: false }
    ],
    description: "You're just one conversation away from a smoother, smarter setup. We’ll personally migrate:",
    features: [
      'All your members (without them noticing a thing)',
      'Your entire course library — videos, PDFs, and modules*',
      'Sales pages and automations',
      'Your email list'
    ],
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: 'Book a FREE migration call',
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: {
            source: 'platform_migration',
          },
        },
      },
    ],
    image: { src: '/images/platform-migration.svg', alt: 'Platform Migration' },
    footerText: 'Migration handled by humans, not bots.'
  },
  reachOutPricingSectionProps: {
    title: [
      { text: 'Try ' },
      { text: 'Graphy', highlight: true },
      { text: ' Today' }
    ],
    description: 'Getting started is easy (and free)',
    image: '/images/pricing-reachout-bg.svg',
    cta: 'Let Graphy build it for me',
    emailLabel: 'Email address',
  },
  testimonySectionProps: {
    title: "Why Indian creators choose Graphy",
  },
};

export const pricingPageData_us: PricingPageDataType = {
  seoMeta: {
    title: "Graphy Pricing | Start Earning As A Creator Today",
    description:
      "Explore free trials and paid plans. Compare Plans. No fine print. No hidden limits. Launch plan ₹1,999 per month, growth plan ₹4,200 per month and rise plan ₹8,400 per month.",
    canonicalPath: ROUTE_PATHS.PRICING,
    countryCode: "",
  },
  pricingHeroSectionProps: {
    heroPill: {
      category: HeroPillCategoryEnum_en.Pricing,
    },
    title: [
      {
        text: "Try out Graphy for free for 14 days",
      },
      {
        text: "",
        highlight: true,
      },
    ],
    description:
      "Whether you're a solo creator or a team of 10, Graphy gives you everything you need to launch, scale, and automate your online teaching business — all in one place.",
    freeCardDetails: {
      title: [
        {
          text: "10% per sale",
          highlight: true,
        },
      ],
      subtitle: "Setup in less than 5 minutes",
      description: "",
      cta: {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        actionType: CtaActionType.SignUp,
        text: "Get started now",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.hero,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      points: [
        "Unlimited learners",
        "No-code website builder",
        "AI Sales, Support & Engage Avatars",
        "Branded mobile apps",
      ],
      cardTitle: "Create your course business for free",
      cardImage: "/images/icons/price-hero.svg", 
      oldPrice: "$49",
      newPrice: "Free",
      trialText: "for 14 days",
      ctaText: "Start your 14-day free trial",
      subtext: "No credit card required",
      startsAtText: "Starts at",
      pointsGrid: true,
    },
    ratings: [
      {
        logo: "/images/categories/home-testimonials/g2-crowd.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
      {
        logo: "/images/categories/home-testimonials/capterra.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
      {
        logo: "/images/categories/home-testimonials/trustpilot.svg",
        rating: "/images/categories/home-testimonials/rating-stars.svg",
      },
    ],
  },
  featureHighlightBar: {
    titles: [
      "Unlimited Products",
      "Unlimited Learners",
      "Branded mobile Apps",
      "AI Avatar",
      "Secure Payments",
      "no code website"
    ],
    starIcon: "/images/icons/starprice.svg"
  },
  planComparisonSectionProps: {
    heroPill: {
      category: HeroPillCategoryEnum_en.Pricing,
    },
    title: [
      {
        text: "Compare Plans",
        highlight: false,
      },
    ],
    description: "Whether you’re just starting or scaling fast, there’s a plan built for you.",
    subDescription: "No fine print. No hidden limits. Just choose your plan and start.",
    billingCycles: [
      { value: BillingCycleType.Month, label: "Monthly" },
      { value: BillingCycleType.Year, label: "Annual" },
    ],
    DiscountText: "Save 28%",
    plans: [
      {
        planName: GraphyPlanType.Launch,
        amount: { month: "$49", year: "$36" },
        bestFor: "Tailored for creators",
        recommened: false,
        ctas: [
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            actionType: CtaActionType.SignUp,
            text: "Get started",
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
              eventProperties: {
                source: SignUpSourceEnum.pricing_section,
              },
            },
            queryParams: {
              destination: OnboardingDestinationEnum.PricingLaunchMonthly,
              skipOnboarding: false,
            },
          },
        ],
        features: [
          "0% transaction fee",
          "Unlimited products",
          "10,000 contacts",
          "1 admin accounts",
          "10,000 live classes minutes per month",
          "Up to 500 webinar participants",
          "Basic integrations",
          "5,000 marketing emails",
          "100 WhatsApp messages"
        ],
      },
      {
        planName: GraphyPlanType.Rise,
        amount: { month: "$99", year: "$74" },
        bestFor: "Built for businesses",
        recommened: true,
        ctas: [
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            actionType: CtaActionType.SignUp,
            text: "Get started",
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
              eventProperties: {
                source: SignUpSourceEnum.pricing_section,
              },
            },
            queryParams: {
              destination: OnboardingDestinationEnum.PricingRiseMonthly,
              skipOnboarding: false,
            },
          },
        ],
        features: [
          "0% transaction fee",
          "Unlimited products",
          "Both Android and IOS apps",
          "25,000 contacts",
          "10 admin accounts",
          "20,000 live classes minutes per month",
          "Up to 500 webinar participants",
          "Basic integrations",
          "10,000 marketing emails",
          "100 WhatsApp messages"
        ],
      },
      {
        planName: GraphyPlanType.Scale,
        amount: { month: "$249", year: "$189" },
        bestFor: "Suited for brands",
        recommened: false,
        ctas: [
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            actionType: CtaActionType.SignUp,
            text: "Get started",
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
              eventProperties: {
                source: SignUpSourceEnum.pricing_section,
              },
            },
            queryParams: {
              destination: OnboardingDestinationEnum.PricingScaleMonthly,
              skipOnboarding: false,
            },
          },
        ],
        features: [
          "0% transaction fee",
          "Unlimited products",
          "Both Android and IOS apps",
          "1,00,000 contacts",
          "25 admin accounts",
          "50,000 live classes minutes per month",
          "Up to 500 webinar participants",
          "API & SSO Support",
          "All integrations",
          "50k Marketing emails",
          "Remove Graphy branding",
          "100 WhatsApp messages"
        ],
      },
    ],
  },
  scheduleDemoSectionProps: {
    title: "Need help? Go ahead and schedule a call with our team!",
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        actionType: CtaActionType.BookDemo,
        text: "Schedule a call",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.pricing_section,
          },
        },
      },
    ],
  },

  // estimationSectionProps: {
  //   title: [
  //     {
  //       text: "Access your course ",
  //       highlight: false,
  //     },
  //     {
  //       text: " potential",
  //       highlight: true,
  //     },
  //   ],
  //   description: "Know your worth and harness the power of your creations",
  //   ctas: [
  //     {
  //       type: ButtonTypeEnum.Gradient,
  //       size: ButtonSizeEnum.Regular,
  //       text: "Start earning now",
  //       actionType: CtaActionType.SignUp,
  //       amplitudeEvent: {
  //         eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
  //         eventProperties: {
  //           source: SignUpSourceEnum.earnings_slider_section,
  //         },
  //       },
  //       queryParams: {
  //         destination: OnboardingDestinationEnum.PricingLaunchMonthly,
  //         skipOnboarding: true,
  //       },
  //     },
  //   ],
  //   audienceSize: {
  //     title: "How big is your audience",
  //     description: "Starting from scratch? Let's estimate your initial base",
  //     defaultValue: 1000,
  //     maximumValue: 10000,
  //   },
  //   offeringPrice: {
  //     title: "Price your offerings",
  //     description: "Estimate the fair price for your valuable content",
  //     defaultValue: 1000,
  //     maximumValue: 10000,
  //   },
  //   perYear: "per year",
  // },

  featuresSectionProps: {
    title: "Every paid plan includes",
    features: [
      // 1st row
      "No-code website builder with stunning templates",
      // "Your own branded mobile app",
      "Accept payments via Stripe or your preferred gateway",
      "Global selling with country-specific pricing",
      "Free custom domain support",
      "Unlimited courses and learners",
      // 2nd row
      "Real-time sales and conversion tracking",
      "Deep marketing insights via built-in analytics",
      "SSL certificates for platform security",
      "Custom team roles and admin access",
      // "Unlimited live sessions and webinars",
      "Unlimited email & push notifications",
      // 3rd row
      "Affiliate tracking baked right in",
      "Live chat and email support",
      "Run discounts and create coupon codes",
      "Offer exclusive memberships to superfans",
      "Award certificates to learners — automatically",
      // "Create custom coupon codes",
    ],
    cta: {
      type: ButtonTypeEnum.Primary,
      size: ButtonSizeEnum.Regular,
      actionType: CtaActionType.SignUp,
      text: "Get Started",
      amplitudeEvent: {
        eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
        eventProperties: {
          source: SignUpSourceEnum.body_end,
        },
      },
      queryParams: {
        destination: OnboardingDestinationEnum.PricingLaunchMonthly,
        skipOnboarding: false,
      },
    },
  },
  graphyAssistSectionProps: {
    title: [
      { text: "Build your AI Brain", highlight: false }
    ],
    description: "Turn your videos, PDFs, tutorials, and FAQs into a powerful AI that knows your business inside out — and runs it like you would.",
    features: [
      "Your own sales, support & engage avatars trained on your content",
      "Custom avatar options that think, talk, and act like you",
      "No extra hiring — just scale yourself",
    ],
    image: {
      src: "/images/pricing-Buildavatar.svg",
      alt: "Graphy Assist",
    },
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: "Train your AI brain",
        actionType: CtaActionType.SignUp,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.ai_avatar,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      {
        type: ButtonTypeEnum.Link,
        size: ButtonSizeEnum.Regular,
        text: "See It in Action",
        actionType: CtaActionType.DelayedHref,
        href: "/ai-avatar",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.LEARN_MORE_TAPPED.LABEL,
          eventProperties: {
            source: "ai_avatar",
          },
        },
      },
    ],
    footerText: "While you sleep, teach, or take a break — your AI sells, supports, and re-engages learners for you."
  },
  reachOutPricingSectionProps: {
    title: [
      { text: 'Try ' },
      { text: 'Graphy', highlight: true },
      { text: ' Today' }
    ],
    description: 'Getting started is easy (and free)',
    image: '/images/pricing-reachout-bg.svg',
    cta: 'Let Graphy build it for me',
    emailLabel: 'Email address',
  },
  faqSectionProps: {
    title: "FAQs",
    description:
      "Explore our help center for detailed information, troubleshooting guides, and more",
    cta: CtaVisitHelpCentre_en,
    faqs: [
      // {
      //   question:
      //     "Do you take 10% on any type of sale or is it different for different offerings?",
      //   answer:
      //     "We take a flat 10% revenue share for all types of offerings, subject to some ongoing offers where we might charge even less. ",
      // },
      {
        question: "Do you take any revenue share on every sale?",
        answer: "No, we do not take any amount of revenue share on your sales.",
      },
      {
        question: "Is there a refund policy?",
        answer:
          "Yes, if you end up not finding a use case for the platform, we offer a refund window of 7 days, no questions asked",
      },
      {
        question: "How can I pay for my Graphy account?",
        answer:
          "We have support for almost all the payment methods in different geographies. Checkout your Graphy dashboard to know more.",
      },
      {
        question: "Are there any academic discounts on Graphy?",
        answer:
          "Please contact our team for more information on any ongoing discounts",
      },
      // {
      //   question: "How does the payout work? ",
      //   answer:
      //     "All your earnings from your sales will be instantly credited into your account.",
      // },
    ],
  },
  pricingOfferingsSectionProps: {
    title: "Check out our offerings",
    description: "Check Out Offerings That Will Help You Grow",
    productOfferings: [], // Will be inferred
    featureOfferings: [], // Will be inferred
  },
  featureCTASectionProps: {
    title: [
      {
        text: "Let's start your",
        highlight: false,
      },
      {
        text: " journey",
        highlight: true,
      },
    ],
    description:
      "Unleash your entrepreneurial spirit. Launch your online business with Graphy and seize success on your own terms",
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        actionType: CtaActionType.SignUp,
        text: "Start for free",
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.body_end,
          },
        },
        queryParams: {
          destination: OnboardingDestinationEnum.PricingLaunchMonthly,
          skipOnboarding: false,
        },
      },
      {
        type: ButtonTypeEnum.Secondary,
        size: ButtonSizeEnum.Regular,
        text: "Book a Demo",
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: {
            source: SignUpSourceEnum.body_end,
          },
        },
      },
    ],
  },
  platformMigrationBannerProps: {
    title: [
      { text: 'Already using another platform?', highlight: true },
      { text: 'We’ll move everything over for you.', highlight: false }
    ],
    description: "You're just one conversation away from a smoother, smarter setup. We’ll personally migrate:",
    features: [
      'All your members (without them noticing a thing)',
      'Your entire course library — videos, PDFs, and modules*',
      'Sales pages and automations',
      'Your email list'
    ],
    ctas: [
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: 'Book a FREE migration call',
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: {
            source: 'platform_migration',
          },
        },
      },
    ],
    image: { src: '/images/platform-migration.svg', alt: 'Platform Migration' },
    footerText: 'Migration handled by humans, not bots.'
  },
  testimonySectionProps: {
    title: "Why creators choose Graphy",
  },
};
